import { useMutation, useQuery } from '@apollo/client/react'
import { Button, Card, Flex, Grid, Text } from '@chakra-ui/react'
import { customerDataApiConfigSchema } from '@helo/shared'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { showToast } from '../../../components/Toast/showToast'
import {
  createCustomerDataApiConfigMutation,
  customerDataApiConfigByIdQuery,
  customerDataApiConfigQuery,
  customerDataApiConfigsQuery,
  rerunCustomerDataApiDeploymentMutation,
} from '../../../services/customerDataApiService'
import { organizationsListQuery } from '../../../services/organizationService'
import {
  AddButton,
  type ConfigListItem,
  DataSourceFields,
  DeletePopover,
  EditorHeader,
  FieldGrid,
  FieldGridCard,
  InputWithLabel,
  LiveConfigStrip,
  type UpdateCustomerDataApiConfigInput,
  VersionRail,
  configsListVariables,
  transformConfigForForm,
} from './DBSubgraphConfigsAdminPage.parts'

export const DBSubgraphConfigsAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const configIdFromUrl = searchParams.get('configId') ?? searchParams.get('id')

  const [configsPollMs, setConfigsPollMs] = useState(0)

  const { data: configsQueryData } = useQuery(customerDataApiConfigsQuery, {
    variables: configsListVariables,
    fetchPolicy: 'network-only',
    pollInterval: configsPollMs,
  })

  const configsList = ((
    configsQueryData as {
      CustomerDataApiConfig_list?: { items?: ConfigListItem[] }
    }
  )?.CustomerDataApiConfig_list?.items ?? []) as ConfigListItem[]

  useEffect(() => {
    setConfigsPollMs(
      configsList.some(
        c =>
          c.latestDeployment?.status === 'PENDING' ||
          c.latestDeployment?.status === 'IN_PROGRESS',
      )
        ? 3000
        : 0,
    )
  }, [configsList])

  const { data: activeConfigData } = useQuery(customerDataApiConfigQuery, {
    fetchPolicy: 'network-only',
  })
  const activeConfig = ((
    activeConfigData as { customerDataApiConfig?: unknown } | undefined
  )?.customerDataApiConfig ?? null) as ConfigListItem | null

  const { data: configByIdData } = useQuery(customerDataApiConfigByIdQuery, {
    variables: { id: configIdFromUrl ?? '' },
    skip: !configIdFromUrl,
    fetchPolicy: 'network-only',
  })
  const configById = ((
    configByIdData as { customerDataApiConfigById?: unknown } | undefined
  )?.customerDataApiConfigById ?? null) as ConfigListItem | null

  const { data: organizationsRaw } = useQuery(organizationsListQuery)
  const organizations = useMemo(
    () =>
      (
        (organizationsRaw as { Organization_list?: { items?: any[] } })
          ?.Organization_list?.items ?? []
      ).filter(
        (organization: { name: string }) =>
          organization.name !== 'allcustomers',
      ),
    [organizationsRaw],
  )

  const [viewedConfigId, setViewedConfigId] = useState<string | null>(null)

  const formMethods = useForm<UpdateCustomerDataApiConfigInput>({
    defaultValues: { subgraphConfigs: [] },
    resolver: standardSchemaResolver(customerDataApiConfigSchema),
    mode: 'onChange',
  })

  const configsRefetchQueries = [
    { query: customerDataApiConfigsQuery, variables: configsListVariables },
    { query: customerDataApiConfigQuery },
  ]

  const selectConfig = (config: ConfigListItem) => {
    formMethods.reset({
      ...transformConfigForForm(config),
      id: undefined,
    })
    setViewedConfigId(config.id)
    setSearchParams({ configId: config.id })
  }

  // When configId in URL and we have fetched that config, load it into the form.
  useEffect(() => {
    if (configIdFromUrl && configById) {
      formMethods.reset({
        ...transformConfigForForm(configById),
        id: undefined,
      })
      setViewedConfigId(configById.id)
    }
  }, [configIdFromUrl, configById])

  // When no configId in URL, use active config as the starting point.
  useEffect(() => {
    if (!configIdFromUrl) {
      if (activeConfig) {
        formMethods.reset({
          ...transformConfigForForm(activeConfig),
          id: undefined,
        })
        setViewedConfigId(activeConfig.id)
      } else {
        formMethods.reset({ subgraphConfigs: [] })
        setViewedConfigId(null)
      }
    }
  }, [configIdFromUrl, activeConfig])

  const {
    fields,
    append: appendSubgraph,
    remove: removeSubgraph,
  } = useFieldArray({
    name: 'subgraphConfigs',
    control: formMethods.control,
  })

  const onSubmit = (_: UpdateCustomerDataApiConfigInput) => {
    const currentFormValues = formMethods.getValues()
    const processedData = {
      subgraphConfigs:
        currentFormValues.subgraphConfigs?.map(subgraph => ({
          id: subgraph?.id?.trim(),
          subgraphName: subgraph?.subgraphName?.trim(),
          path: subgraph?.path?.trim(),
          customerDataSources:
            subgraph?.customerDataSources?.map(dataSource => ({
              id: dataSource?.id?.trim() ?? '',
              organizationName: dataSource?.organizationName?.trim() ?? '',
              currentVersion: dataSource?.currentVersion?.trim() ?? '',
              isMainIntrospectionDB: dataSource.isMainIntrospectionDB ?? false,
              customerDatabases:
                dataSource.customerDatabases?.map(
                  (database: {
                    id?: string
                    version?: string
                    host?: string
                    port?: number
                    databaseName?: string
                    cubeApiUrl?: string | null
                  }) => ({
                    id: database?.id?.trim() ?? '',
                    version: database?.version?.trim() ?? '',
                    host: database?.host?.trim() ?? '',
                    port: database?.port ?? 5432,
                    databaseName: database?.databaseName?.trim() ?? '',
                    cubeApiUrl:
                      database?.cubeApiUrl != null
                        ? database.cubeApiUrl.trim()
                        : null,
                  }),
                ) || [],
            })) || [],
        })) || [],
    }

    void mutateCreateCustomerDataApiConfig({
      variables: {
        input: { subgraphConfigs: processedData.subgraphConfigs },
      },
    })
  }

  const [mutateCreateCustomerDataApiConfig, { loading: isSubmitting }] =
    useMutation(createCustomerDataApiConfigMutation, {
      refetchQueries: configsRefetchQueries,
      awaitRefetchQueries: true,
      onCompleted: data => {
        const newConfig = (
          data as { createCustomerDataApiConfig?: ConfigListItem }
        )?.createCustomerDataApiConfig
        if (newConfig) {
          selectConfig(newConfig)
        }
        showToast({
          title: 'Saved. Pipeline started.',
          description:
            'A new pipeline run was created. Track it from the Deployments page.',
          variant: 'success',
        })
      },
      onError: () =>
        showToast({
          title: 'Failed to save config',
          variant: 'error',
        }),
    })

  const [mutateRerunLatest, { loading: isRerunning }] = useMutation(
    rerunCustomerDataApiDeploymentMutation,
    {
      refetchQueries: configsRefetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        showToast({
          title: 'New pipeline run created',
          description: 'Tracking it on the Deployments page.',
          variant: 'success',
        })
      },
      onError: (err: Error) =>
        showToast({
          title: 'Failed to start pipeline run',
          description: err.message,
          variant: 'error',
        }),
    },
  )

  const handleAddSubgraph = () => {
    appendSubgraph({
      id: '',
      subgraphName: '',
      path: '',
      customerDataSources: [],
    } as UpdateCustomerDataApiConfigInput['subgraphConfigs'][0])
  }

  const handleCreateNewConfig = () => {
    setViewedConfigId(null)
    if (activeConfig) {
      formMethods.reset({
        ...transformConfigForForm(activeConfig),
        id: undefined,
      })
    } else {
      formMethods.reset({ subgraphConfigs: [] })
    }
  }

  const viewedConfig = useMemo(
    () => configsList.find(c => c.id === viewedConfigId) ?? null,
    [configsList, viewedConfigId],
  )
  const isViewingActive =
    viewedConfigId != null && viewedConfigId === activeConfig?.id
  const isDirty = formMethods.formState.isDirty

  return (
    <AdminPageLayout pageTitle="Customer Data API Config">
      <Flex direction="column" gap="12px" pb="20px">
        <LiveConfigStrip
          activeConfig={activeConfig}
          onOpenInEditor={() => {
            if (activeConfig) {
              formMethods.reset({
                ...transformConfigForForm(activeConfig),
                id: undefined,
              })
              setViewedConfigId(activeConfig.id)
              setSearchParams({ configId: activeConfig.id })
            }
          }}
        />

        <Flex gap="12px" align="stretch">
          <VersionRail
            configs={configsList}
            activeConfigId={activeConfig?.id ?? null}
            viewedConfigId={viewedConfigId}
            onSelect={selectConfig}
          />

          <Flex direction="column" flex="1" gap="12px" minW="0">
            <FormProvider {...formMethods}>
              <Flex
                as="form"
                onSubmit={formMethods.handleSubmit(onSubmit, () => {
                  showToast({
                    title: 'Please fix validation errors before saving',
                    variant: 'error',
                  })
                })}
                direction="column"
                gap="10px"
              >
                <EditorHeader
                  viewedConfig={viewedConfig}
                  isViewingActive={isViewingActive}
                  isDirty={isDirty}
                  isSubmitting={isSubmitting}
                  isRerunning={isRerunning}
                  onRerunThisVersion={() => {
                    const depId = viewedConfig?.latestDeployment?.id
                    if (depId)
                      void mutateRerunLatest({
                        variables: { deploymentId: depId },
                      })
                  }}
                />

                <Card p="10px" bg="presets.neutral.5">
                  <Flex direction="column" gap="2px">
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      How this page works
                    </Text>
                    <Text
                      fontSize="11px"
                      lineHeight="1.35"
                      color="presets.neutral.70"
                    >
                      Saving creates a new config version and immediately starts
                      a new pipeline run. The live runtime changes only after a
                      successful run. Re-running a historical version reuses
                      that exact saved config and creates a new deployment
                      attempt.
                    </Text>
                  </Flex>
                </Card>

                {fields.length > 0 && (
                  <Grid
                    templateColumns="repeat(auto-fill, minmax(680px, 1fr))"
                    gap="16px"
                  >
                    {fields.map((subgraph, subgraphIndex) => (
                      <FieldGridCard key={subgraph.id}>
                        <DeletePopover
                          triggerLabel="Delete Subgraph"
                          onDelete={() => removeSubgraph(subgraphIndex)}
                        />
                        <FieldGrid mb="16px">
                          <InputWithLabel
                            label="ID"
                            name={`subgraphConfigs.${subgraphIndex}.id`}
                          />
                          <InputWithLabel
                            label="Name"
                            name={`subgraphConfigs.${subgraphIndex}.subgraphName`}
                          />
                          <InputWithLabel
                            label="Path"
                            name={`subgraphConfigs.${subgraphIndex}.path`}
                          />
                        </FieldGrid>
                        <DataSourceFields
                          subgraphIndex={subgraphIndex}
                          organizations={organizations}
                        />
                      </FieldGridCard>
                    ))}
                  </Grid>
                )}
                <AddButton label="Add Subgraph" onClick={handleAddSubgraph} />
              </Flex>
            </FormProvider>
          </Flex>
        </Flex>
      </Flex>
    </AdminPageLayout>
  )
}
